// Counting one delivery against a PO. The scan box always has focus: a scanned barcode (or SKU, or
// vendor SKU) adds 1 to its line, "12*CODE" adds 12, and counts can be typed too. Damaged or wrong
// units go in Rejected with a reason and a photo. The count lives in this browser until it's posted;
// posting sends it once (the receipt's id is made when counting starts).
// A scan that's on another open PO from the same vendor offers to switch to it (this count stays saved), and
// "Everything still due arrived" counts every line's due as good and posts, after a confirmation naming the totals.
import { ArrowLeft, ArrowRightLeft, Camera, CheckCheck, CircleAlert, Minus, Plus, ScanLine, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { numberOf } from "../../../../shared/calc.ts";
import type { Item, PoLine } from "../../../../shared/db.gen.ts";
import { PO_STATUS_LABELS } from "../../../../shared/po.ts";
import {
  backersLeft,
  expectedHere,
  matchScan,
  outstanding,
  overBy,
  REJECT_REASON_LABELS,
  REJECT_REASONS,
  type RejectReason,
} from "../../../../shared/receiving.ts";
import { stockNote } from "../../../../shared/stock.ts"; // Stock everywhere ---
import { qty } from "../../lib/format.ts";
import { mutateWithResult } from "../../lib/mutate.ts";
import { uploadPhoto } from "../../lib/photos.ts";
import { Link, navigate, setSearch, useSearch } from "../../lib/router.tsx";
import { useRow } from "../../lib/sync.ts";
import { Badge, Button, cx, EmptyState, Input, ItemName, Select, Textarea, Thumb } from "../../ui/controls.tsx";
import { useFileDrop } from "../../ui/FileDrop.tsx";
import { isTyping } from "../../ui/ListPage.tsx";
import { PrintMenu } from "../../ui/PrintMenu.tsx";
import { useStockIndex } from "../../ui/StockColumns.tsx"; // Stock everywhere ---
import { toast } from "../../ui/toast.tsx";
import { STATUS_TONE, vendorLabel } from "../po/lib.ts";
import { poLabels, TrackingText } from "../po/Tracking.tsx";
import {
  beep,
  clearDraft,
  type Draft,
  emptyCount,
  type LineCount,
  linePhoto,
  loadDraft,
  parseScan,
  type ReceivingLine,
  saveDraft,
  useReceiving,
} from "./lib.ts";
import { ReceiptList } from "./ReceiptList.tsx";

type Row = { line: ReceivingLine; item: Item | undefined; count: LineCount };

const fmt = (n: number) => qty(n) || "0";

export function CountPo({ poId }: { poId: string }) {
  const po = useRow("po", poId);
  const r = useReceiving();
  const [draft, setDraft] = useState<Draft>(() => loadDraft(poId));
  const [scan, setScan] = useState("");
  const [hit, setHit] = useState<{ id: string; at: number } | null>(null);
  const [posting, setPosting] = useState(false);
  // A scan for another of this vendor's open POs: where it is, and what was scanned (counted there on switching).
  const [elsewhere, setElsewhere] = useState<{ poId: string; number: string; scan: string; code: string } | null>(null);
  const scanRef = useRef<HTMLInputElement>(null);
  const firstScan = useSearch().get("scan");

  useEffect(() => saveDraft(poId, draft), [poId, draft]);

  const rows = useMemo<Row[]>(
    () =>
      (r.linesByPo.get(poId) ?? []).map((line) => ({
        line,
        item: r.itemById.get(line.item_id),
        count: draft.counts[line.id] ?? emptyCount(null),
      })),
    [r, poId, draft.counts],
  );

  const setCount = (line: PoLine, change: (c: LineCount) => LineCount) =>
    setDraft((d) => {
      // The first count on a line remembers what it had received, so a receipt posted meanwhile is noticed.
      const current = d.counts[line.id] ?? emptyCount(Number(line.qty_received));
      const next = change({ ...current, seen: current.seen ?? Number(line.qty_received) });
      return { ...d, counts: { ...d.counts, [line.id]: { ...next, good: Math.max(0, next.good), bad: Math.max(0, next.bad) } } };
    });

  const applyScan = (text: string) => {
    const { qty: n, code } = parseScan(text);
    const target = matchScan(
      code,
      rows.map((row) => ({
        id: row.line.id,
        barcode: row.item?.barcode ?? "",
        sku: row.line.sku,
        vendor_sku: row.line.vendor_sku,
        done: Number(row.line.qty_received) + row.count.good >= expectedHere(row.line),
      })),
    );
    // On another open PO? One where it's still due comes first. A scan for a line here that's already complete goes to
    // the same vendor's PO still waiting for it (offered), rather than counting over here.
    const hitOn = (p: (typeof r.open)[number]) =>
      matchScan(
        code,
        (r.linesByPo.get(p.id) ?? []).map((l) => ({
          id: l.id,
          barcode: r.itemById.get(l.item_id)?.barcode ?? "",
          sku: l.sku,
          vendor_sku: l.vendor_sku,
          done: outstanding(l) === 0,
        })),
      );
    if (!target || target.done) {
      const matches = r.open.flatMap((p) => {
        const hit = p.id !== poId ? hitOn(p) : undefined;
        return hit ? [{ p, due: !hit.done }] : [];
      });
      const sameVendor = matches.filter((m) => m.p.vendor_id === po?.vendor_id).sort((x, y) => Number(y.due) - Number(x.due));
      const offer = sameVendor[0];
      if (offer && (!target || offer.due)) {
        beep(false);
        setElsewhere({ poId: offer.p.id, number: offer.p.number, scan: text, code });
        return;
      }
      if (!target) {
        beep(false);
        const other = matches.find((m) => m.due)?.p ?? matches[0]?.p;
        toast.error(
          other
            ? `${code} isn't on ${po?.number ?? "this PO"}: it's on ${other.number} from ${vendorLabel(r.vendorById.get(other.vendor_id))}`
            : `${code} isn't on ${po?.number ?? "this PO"}`,
        );
        return;
      }
    }
    setElsewhere(null);
    const row = rows.find((x) => x.line.id === target.id) as Row;
    beep(true);
    setCount(row.line, (c) => ({ ...c, good: c.good + n }));
    setHit({ id: row.line.id, at: Date.now() });
    document.getElementById(`line-${row.line.id}`)?.scrollIntoView({ block: "nearest" });
  };

  // Opened from a scan on the PO list: count that scan here.
  const applied = useRef(false);
  useEffect(() => {
    if (!firstScan || applied.current || !rows.length) return;
    applied.current = true;
    applyScan(firstScan);
    setSearch({ scan: null });
  });

  // A scanner types into whatever has focus; anything typed outside a field goes to the scan box.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        post();
        return;
      }
      if (isTyping(e.target) || e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1) return;
      scanRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!po) return <EmptyState title="That PO no longer exists" />;
  const vendor = r.vendorById.get(po.vendor_id);
  const shipTo = r.locations.find((l) => l.id === po.ship_to_location_id);
  const countedNow = rows.filter((x) => x.count.good > 0 || x.count.bad > 0);
  const counted = countedNow;
  const units = counted.reduce((n, x) => n + x.count.good, 0);
  const rejected = counted.reduce((n, x) => n + x.count.bad, 0);
  const overNow = counted.filter((x) => overBy(x.line, x.count.good) > 0);
  const receipts = r.recent.filter((x) => x.po_id === po.id);
  const closed = po.status === "closed" || po.status === "cancelled" || po.status === "draft";
  // Pre-sales --- "nothing else is coming" on a make PO closes HHL's share only: the backers' units are still the CM's.
  const forBackers = po.kind === "make" ? rows.reduce((t, x) => t + backersLeft(x.line), 0) : 0;
  const closesText = `Nothing else is coming to HHL. ${vendorLabel(vendor)} still has ${fmt(forBackers)} to build for backers; ${po.number} stays open until they're reported built or shipped.`;

  // Every line still due, counted as all good (what's counted already stays if it's more): the "everything arrived" count.
  const due = rows.filter((x) => outstanding(x.line) > 0);
  const dueUnits = due.reduce((n, x) => n + outstanding(x.line), 0);
  async function everything() {
    if (!po || posting || !due.length) return;
    if (
      !confirm(
        `Everything still due on ${po.number} arrived, all good?\n\n${due.length} ${due.length === 1 ? "line" : "lines"} · ${fmt(dueUnits)} units are posted as received now${rejected ? ` (and the ${fmt(rejected)} rejected you counted)` : ""}.`,
      )
    )
      return;
    const next = { ...draft.counts };
    for (const x of due) {
      const c = next[x.line.id] ?? emptyCount(Number(x.line.qty_received));
      next[x.line.id] = { ...c, seen: c.seen ?? Number(x.line.qty_received), good: Math.max(c.good, outstanding(x.line)) };
    }
    // The saved count changes only if it posts (post() clears it then): a "no" to one of its questions leaves it as it was.
    await post(next);
  }

  async function post(override?: Draft["counts"]) {
    if (!po || posting) return;
    // "Everything arrived" posts the counts it just made (the screen hasn't drawn them yet).
    const counted = override
      ? rows.flatMap((x) => {
          const count = override[x.line.id];
          return count && (count.good > 0 || count.bad > 0) ? [{ ...x, count }] : [];
        })
      : countedNow;
    const over = counted.filter((x) => overBy(x.line, x.count.good) > 0);
    const short = rows.filter((x) => Number(x.line.qty_received) + (override?.[x.line.id]?.good ?? x.count.good) < expectedHere(x.line));
    if (!counted.length) return toast.error("Count something first");
    // Someone posted a receipt for this PO while this count was going on: say so before adding to it.
    const moved = counted.filter((x) => x.count.seen !== null && x.count.seen !== Number(x.line.qty_received));
    if (moved.length) {
      const list = moved.map((x) => `${x.line.sku || x.line.name}: now ${fmt(Number(x.line.qty_received))} received`).join("\n");
      if (!confirm(`Someone posted a receipt for ${po.number} while you were counting:\n${list}\n\nAre your counts for different boxes? OK posts them on top.`))
        return;
    }
    if (over.length) {
      const list = over.map((x) => `${x.line.sku || x.line.name}: +${fmt(overBy(x.line, x.count.good))}`).join("\n");
      if (!confirm(`More than was ordered:\n${list}\n\nPost anyway?`)) return;
    }
    if (
      draft.closes &&
      short.length &&
      !confirm(
        forBackers > 0
          ? `${closesText}\n\n${short.length} ${short.length === 1 ? "line is" : "lines are"} short for HHL: nothing else will be expected here.`
          : `Close ${po.number} with ${short.length} ${short.length === 1 ? "line" : "lines"} short? Nothing else will be expected.`,
      )
    ) {
      return;
    }
    setPosting(true);
    const { ok, result } = await mutateWithResult("receipt.post", {
      id: draft.id,
      po_id: po.id,
      lines: counted.map((x) => ({
        po_line_id: x.line.id,
        qty_received: x.count.good,
        qty_rejected: x.count.bad,
        reject_reason: x.count.bad > 0 ? x.count.reason : ("" as const),
        photo_url: x.count.photo,
        notes: x.count.notes,
        seen_received: Number(x.line.qty_received),
      })),
      notes: draft.notes,
      closes_po: draft.closes,
      allow_over: over.length > 0,
    });
    setPosting(false);
    if (!ok) return;
    const { number, status, notes } = result as { number: string; status: string; notes?: string[] };
    clearDraft(po.id);
    toast.ok(`Posted ${number}${status === "received" ? `; ${po.number} is received` : ""}`);
    for (const n of notes ?? []) toast.info(n);
    navigate("/receiving");
  }

  const reset = () => {
    if (counted.length && !confirm("Clear this count?")) return;
    clearDraft(po.id);
    setDraft(loadDraft(po.id));
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-line px-4 py-3">
        <Link to="/receiving" className="rounded p-1 text-muted hover:bg-hover hover:text-text" title="Back to receiving">
          <ArrowLeft className="size-5" />
        </Link>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[16px] font-semibold">{po.number}</span>
            <span className="truncate text-[15px] font-semibold">{vendorLabel(vendor)}</span>
            <Badge tone={STATUS_TONE[po.status]}>{PO_STATUS_LABELS[po.status]}</Badge>
          </div>
          <div className="text-[12px] text-muted">
            {rows.length} {rows.length === 1 ? "line" : "lines"}
            {shipTo ? ` · to ${shipTo.name}` : ""}
            {po.vendor_ref ? ` · their ref ${po.vendor_ref}` : ""}
          </div>
          <TrackingText po={po} className="text-[12px] text-muted" />
        </div>
        <span className="sm:ml-auto">
          <PrintMenu label="Labels" items={poLabels(po.id)} />
        </span>
        <form
          className="relative order-last w-full sm:order-none sm:w-80"
          onSubmit={(e) => {
            e.preventDefault();
            if (scan.trim()) applyScan(scan);
            setScan("");
          }}
        >
          <ScanLine className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-faint" />
          <Input
            ref={scanRef}
            autoFocus
            value={scan}
            onChange={(e) => setScan(e.target.value)}
            placeholder="Scan, or type a SKU (12*SKU adds 12)"
            className="h-10 pl-8"
            aria-label="Scan"
            disabled={closed}
          />
        </form>
      </div>

      {elsewhere && !closed && (
        <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-warn/30 bg-warn-soft px-4 py-2 text-[13px]">
          <ArrowRightLeft className="size-4 shrink-0 text-warn" />
          <span className="min-w-0 flex-1">
            <span className="font-mono font-medium">{elsewhere.code}</span> isn't on {po.number}: it's on{" "}
            <span className="font-mono font-medium">{elsewhere.number}</span>, also from {vendorLabel(vendor)}. Is this box for that PO?
          </span>
          <Button size="sm" variant="ghost" onClick={() => setElsewhere(null)}>
            Stay here
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => navigate(`/receiving?po=${elsewhere.poId}&scan=${encodeURIComponent(elsewhere.scan)}`)}
            title={`Opens ${elsewhere.number} and counts the scan there; what you've counted here stays saved`}
          >
            Switch to {elsewhere.number}
          </Button>
        </div>
      )}
      {closed ? (
        <EmptyState title={`${po.number} is ${PO_STATUS_LABELS[po.status].toLowerCase()}`}>Only sent POs can be received.</EmptyState>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="divide-y divide-line">
            {rows.map((row) => (
              <LineRow
                key={row.line.id}
                row={row}
                poNumber={po.number}
                flash={hit?.id === row.line.id ? hit.at : 0}
                onChange={(change) => setCount(row.line, change)}
              />
            ))}
          </div>
          {receipts.length > 0 && (
            <div className="mt-4 border-t border-line">
              <h2 className="px-4 pt-3 pb-1 text-[11px] font-medium tracking-wide text-faint uppercase">Already received</h2>
              <ReceiptList receipts={receipts} />
            </div>
          )}
        </div>
      )}

      {!closed && (
        <div className="flex shrink-0 flex-wrap items-end gap-3 border-t border-line bg-surface-2/50 px-4 py-3">
          <Textarea
            value={draft.notes}
            onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
            placeholder="Notes for this delivery (cartons, carrier, condition)"
            className="min-h-9 flex-1 basis-64"
            rows={1}
          />
          <label
            className="flex h-9 items-center gap-2 text-[13px]"
            title="The vendor isn't sending anything else: the PO is received even if short, and Shopify stops expecting the rest"
          >
            <input type="checkbox" checked={draft.closes} onChange={(e) => setDraft((d) => ({ ...d, closes: e.target.checked }))} />
            Nothing else is coming
          </label>
          {draft.closes && forBackers > 0 && <div className="basis-full text-[12px] text-muted">{closesText}</div>}
          <div className="flex h-9 items-center gap-2 text-[13px] text-muted">
            {counted.length ? (
              <>
                {counted.length} {counted.length === 1 ? "line" : "lines"} · {fmt(units)} good
                {rejected > 0 && <span className="text-danger"> · {fmt(rejected)} rejected</span>}
                {overNow.length > 0 && <Badge tone="warn">{overNow.length} over</Badge>}
              </>
            ) : (
              "Nothing counted yet"
            )}
          </div>
          {counted.length > 0 && (
            <Button variant="ghost" onClick={reset}>
              Clear
            </Button>
          )}
          {due.length > 0 && (
            <Button
              icon={<CheckCheck className="size-4" />}
              onClick={everything}
              disabled={posting}
              title={`Count every line's due as arrived, all good (${due.length} ${due.length === 1 ? "line" : "lines"} · ${fmt(dueUnits)} units), and post`}
            >
              Everything still due arrived
            </Button>
          )}
          <Button variant="primary" kbd="Ctrl ↵" onClick={() => post()} disabled={posting || !counted.length}>
            {posting ? "Posting…" : "Post receipt"}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Stock everywhere --- what HHL has of it now and what's still coming (this PO included), so a count can be checked
 * against the shelf (Mitch's inventory manager: "the number I ordered, as well as in stock").
 */
function StockLine({ itemId }: { itemId: string }) {
  const ix = useStockIndex();
  const s = ix.atHhl.get(itemId);
  const inc = ix.incoming.get(itemId);
  const note = stockNote(s);
  return (
    <div className="truncate text-[12px] text-muted">
      <span title={note ?? undefined}>
        {fmt(s?.available ?? 0)} available · {fmt(s?.onHand ?? 0)} on hand
      </span>
      {inc?.incoming ? ` · ${fmt(inc.incoming)} incoming` : ""}
    </div>
  );
}

function LineRow({ row, poNumber, flash, onChange }: { row: Row; poNumber: string; flash: number; onChange: (change: (c: LineCount) => LineCount) => void }) {
  const { line, item, count } = row;
  const [rejecting, setRejecting] = useState(count.bad > 0 || !!count.photo);
  const [uploading, setUploading] = useState(false);
  const [lit, setLit] = useState(false);
  // The shared file box's picker (ui/FileDrop.tsx), opening the tablet's back camera
  const photo = useFileDrop({ onFiles: ([f]) => upload(f), accept: "image/*", multiple: false, capture: "environment", disabled: uploading });

  // A scanned line lights up for a moment.
  useEffect(() => {
    if (!flash) return;
    setLit(true);
    const t = setTimeout(() => setLit(false), 600);
    return () => {
      clearTimeout(t);
      setLit(false); // another line was scanned meanwhile
    };
  }, [flash]);

  // Pre-sales --- a make PO's units for backers go straight from the CM: only the rest is expected here.
  const ordered = expectedHere(line);
  const toBackers = Number(line.qty_direct ?? 0);
  const before = Number(line.qty_received);
  const due = outstanding(line);
  const after = before + count.good;
  const extra = overBy(line, count.good);
  const state =
    count.good === 0 && count.bad === 0
      ? null
      : extra > 0
        ? { tone: "warn" as const, text: `+${fmt(extra)} over` }
        : after >= ordered
          ? { tone: "ok" as const, text: "Complete" }
          : { tone: "neutral" as const, text: `${fmt(ordered - after)} short` };

  const upload = async (file: File) => {
    setUploading(true);
    try {
      const url = await uploadPhoto(file, `${poNumber} ${line.sku} rejected`);
      onChange((c) => ({ ...c, photo: url }));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const number = (value: string) => {
    const n = numberOf(value);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };

  return (
    <div
      id={`line-${line.id}`}
      className={cx("px-4 py-3 transition-colors duration-500", lit ? "bg-accent-soft" : count.good || count.bad ? "bg-surface" : "")}
    >
      <div className="flex flex-wrap items-center gap-3">
        <Thumb src={linePhoto(line, item)} size={56} className="rounded-md" />
        <div className="min-w-0 flex-1 basis-48">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[13px] font-semibold">{line.sku || "No SKU"}</span>
            {item?.source === "shopify" && <Badge tone="accent">Shopify</Badge>}
          </div>
          <ItemName name={line.name} item={item} className="block" />
          <div className="truncate text-[12px] text-faint">
            {[line.vendor_sku && `Vendor: ${line.vendor_sku}`, item?.barcode && `Barcode ${item.barcode}`].filter(Boolean).join(" · ")}
          </div>
          <StockLine itemId={line.item_id} />
          {line.notes && <div className="truncate text-[12px] text-muted">{line.notes}</div>}
        </div>
        <div className="w-36 shrink-0 text-right text-[12px] text-muted">
          <div title={toBackers > 0 ? `${fmt(Number(line.qty))} ordered; ${fmt(toBackers)} of them go straight from the CM to backers` : undefined}>
            {toBackers > 0 ? "Expected" : "Ordered"} <span className="font-mono text-text">{fmt(ordered)}</span>
          </div>
          {toBackers > 0 && <div className="text-[11px] text-faint">{fmt(toBackers)} go to backers</div>}
          {before > 0 && (
            <div>
              Received <span className="font-mono">{fmt(before)}</span>
            </div>
          )}
          <div>
            Due <span className={cx("font-mono", due ? "text-text" : "")}>{fmt(due)}</span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            className="flex size-9 items-center justify-center rounded-md border border-line hover:bg-hover"
            onClick={() => onChange((c) => ({ ...c, good: c.good - 1 }))}
            aria-label="One less"
          >
            <Minus className="size-4" />
          </button>
          <div className="w-20 shrink-0">
            <Input
              inputMode="decimal"
              value={count.good ? String(count.good) : ""}
              placeholder="0"
              onChange={(e) => onChange((c) => ({ ...c, good: number(e.target.value) }))}
              onFocus={(e) => e.target.select()}
              className="h-9 text-center font-mono text-[15px]"
              aria-label={`Count of ${line.sku}`}
            />
          </div>
          <button
            type="button"
            className="flex size-9 items-center justify-center rounded-md border border-line hover:bg-hover"
            onClick={() => onChange((c) => ({ ...c, good: c.good + 1 }))}
            aria-label="One more"
          >
            <Plus className="size-4" />
          </button>
          {due > 0 && count.good < due && (
            <Button size="sm" variant="ghost" onClick={() => onChange((c) => ({ ...c, good: due }))} title="Everything still due arrived">
              All {fmt(due)}
            </Button>
          )}
        </div>
        <div className="flex w-32 shrink-0 items-center justify-end gap-1">
          {state && <Badge tone={state.tone}>{state.text}</Badge>}
          {count.bad > 0 && <Badge tone="danger">{fmt(count.bad)} rejected</Badge>}
        </div>
        <button
          type="button"
          onClick={() => setRejecting((v) => !v)}
          className={cx("rounded p-1.5 hover:bg-hover", rejecting || count.bad ? "text-danger" : "text-faint")}
          title="Damaged or wrong: reject some"
        >
          <CircleAlert className="size-4" />
        </button>
      </div>
      {rejecting && (
        <div className="mt-2 ml-[68px] flex flex-wrap items-center gap-2 rounded-md border border-line bg-surface-2/60 p-2">
          <span className="text-[12px] text-muted">Rejected</span>
          <div className="w-20 shrink-0">
            <Input
              inputMode="decimal"
              value={count.bad ? String(count.bad) : ""}
              placeholder="0"
              onChange={(e) => onChange((c) => ({ ...c, bad: number(e.target.value) }))}
              className="h-8 text-center font-mono"
              aria-label={`Rejected ${line.sku}`}
            />
          </div>
          <div className="w-32 shrink-0">
            <Select
              value={count.reason}
              onChange={(e) => onChange((c) => ({ ...c, reason: e.target.value as RejectReason }))}
              options={REJECT_REASONS.map((r) => ({ value: r, label: REJECT_REASON_LABELS[r] }))}
              className="h-8"
              aria-label="Reason"
            />
          </div>
          {photo.picker}
          {count.photo ? (
            <span className="flex items-center gap-1">
              <a href={count.photo} target="_blank" rel="noreferrer">
                <Thumb src={count.photo} size={32} />
              </a>
              <button
                type="button"
                className="rounded p-1 text-faint hover:bg-hover"
                onClick={() => onChange((c) => ({ ...c, photo: "" }))}
                aria-label="Remove photo"
              >
                <X className="size-3.5" />
              </button>
            </span>
          ) : (
            <Button size="sm" icon={<Camera className="size-3.5" />} onClick={photo.choose} disabled={uploading}>
              {uploading ? "Uploading…" : "Photo"}
            </Button>
          )}
          <Input
            value={count.notes}
            onChange={(e) => onChange((c) => ({ ...c, notes: e.target.value }))}
            placeholder="What's wrong with them?"
            className="h-8 min-w-40 flex-1"
            aria-label="Notes"
          />
        </div>
      )}
    </div>
  );
}
